/**
 * OutputPanel Component
 *
 * Panel for generating and exporting compliance reports.
 * Currently opens a sample PDF from the output folder.
 *
 * TODO: Future Database/Generation Implementation
 * - Replace static PDF with dynamic report generation
 * - Integrate with report generation service/API
 * - Add progress indicator for generation process
 * - Allow customization of report parameters
 * - Support multiple export formats (PDF, Excel, Word)
 */

import React, { useState, useEffect, useMemo } from "react";
import { useRulesStore } from "../../store/rulesStore";
import { EmailPopupModal } from "../common/EmailPopupModal";
import { HelpPanel } from "../help/HelpPanel";
import { loadComplianceReportsForModel } from "../../services/dataService";
import { useViewerStore } from "../../store/viewerStore";
import type { RuleInstance } from "../../types/rules";
import type { ValidationResult } from "../../types/rules";

interface ValidationReport {
  // Old format
  toilet_element_id?: string | number;
  validation_timestamp?: string;
  jurisdiction?: string;
  standard?: string;
  standard_section?: string;
  compartment_type_detection?: {
    detected_type?: string;
    detection_method?: string;
    confidence?: string;
    notes?: string;
  };
  regulations?: Array<{
    regulation_id: string;
    section: string;
    title: string;
    requirement?: string;
    description?: string;
    status: string;
    measured_value?: string | null;
    required_value?: string;
    jersey_specific?: boolean;
    notes?: string;
    confidence?: string;
    severity?: string | null;
    entities_used?: Array<{
      entity_name?: string;
      dbId?: number;
      elementId?: number;
      [key: string]: any;
    }>;
  }>;
  summary?: {
    total_regulations?: number;
    passed_count?: number;
    failed_count?: number;
    not_tested_count?: number;
  };
  // New v3 format
  metadata?: {
    element_id?: number;
    element_name?: string;
    element_type?: string;
    validation_date?: string;
    standard?: string;
    jurisdiction?: string;
    section?: string;
  };
  // New v4 format
  validation_metadata?: {
    room_id?: string | number;
    room_name?: string;
    standard?: string;
    jurisdiction?: string;
    section?: string;
    validation_date?: string;
  };
  validation_results?: Array<{
    regulation_id: string;
    section: string;
    title: string;
    description?: string;
    requirement?: string;
    status: string;
    measured_value?: string | null;
    required_value?: string;
    jersey_specific?: boolean;
    notes?: string;
    confidence?: string;
    severity?: string | null;
    entities_used?: Array<{
      entity_name?: string;
      dbId?: number;
      elementId?: number;
      [key: string]: any;
    }>;
  }>;
  regulation_validation_results?: Array<{
    regulation_id: string;
    title: string;
    requirement?: string;
    status: string;
    measured_value?: string | null;
    required_value?: string;
    jersey_specific?: boolean;
    notes?: string;
    severity?: string | null;
    entities_used?: Array<{
      entity_name?: string;
      dbId?: number;
      elementId?: number;
      [key: string]: any;
    }>;
  }>;
}

/**
 * Determines which PDF file to send based on active compliance rules
 * @param activeRules - Array of active rule instances
 * @returns Object with filename and display name for the PDF
 */
const getPDFForActiveRules = (activeRules: RuleInstance[]) => {
  const hasToiletRule = activeRules.some(
    (rule) => rule.templateId === "ada-toilet-side-wall-clearance"
  );
  const hasGrabBarRule = activeRules.some(
    (rule) => rule.templateId === "ada-side-wall-grab-bar"
  );

  if (hasToiletRule && hasGrabBarRule) {
    return {
      filename: "toilets-grab-bars.pdf",
      displayName: "Toilets & Grab Bars Compliance Report",
    };
  } else if (hasToiletRule) {
    return {
      filename: "toilets.pdf",
      displayName: "Toilets Compliance Report",
    };
  } else if (hasGrabBarRule) {
    return {
      filename: "grab-bars.pdf",
      displayName: "Grab Bars Compliance Report",
    };
  } else {
    // Default fallback
    return {
      filename: "compliance-report.pdf",
      displayName: "Compliance Report",
    };
  }
};

interface OutputPanelProps {
  onNavigateToCelebration?: () => void;
  onNavigateToRisks?: () => void;
}

export function OutputPanel({
  onNavigateToCelebration,
  onNavigateToRisks,
}: OutputPanelProps = {}) {
  const {
    validationSummary,
    validationResults: storeValidationResults,
    getActiveRules,
  } = useRulesStore();

  // Check if validation has been run from the Compliance Hub
  // validationSummary is only set by runValidation(), so it's the source of truth
  const hasRunValidation = !!validationSummary;
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const { selectedModelId, selectedJurisdiction } = useViewerStore();

  // State for validation reports (same as RiskPanel)
  const [validationReports, setValidationReports] = useState<
    ValidationReport[]
  >([]);
  const [isLoadingReports, setIsLoadingReports] = useState(true);

  // Load validation reports for the selected model
  useEffect(() => {
    const loadValidationReports = async () => {
      setIsLoadingReports(true);
      try {
        const reports = await loadComplianceReportsForModel<ValidationReport>(selectedModelId, selectedJurisdiction);

        setValidationReports(reports);
      } catch (error) {
        console.error("Error loading validation reports:", error);
      } finally {
        setIsLoadingReports(false);
      }
    };

    loadValidationReports();
  }, [selectedModelId, selectedJurisdiction]);

  // Convert validation reports to ValidationResult format (same as RiskPanel)
  const validationResults = useMemo(() => {
    const results: ValidationResult[] = [];

    validationReports.forEach((report) => {
      // Handle v4 format (regulation_validation_results), v3 format (validation_results), and old format (regulations)
      const regulations =
        report.regulation_validation_results ||
        report.validation_results ||
        report.regulations ||
        [];
      if (regulations.length === 0) return;

      // Get toilet ID from v4, v3, or old format
      const toiletId = report.validation_metadata?.room_id
        ? String(report.validation_metadata.room_id)
        : report.metadata?.element_id
          ? String(report.metadata.element_id)
          : String(report.toilet_element_id || "unknown");

      regulations.forEach((regulation) => {
        const regStatus = regulation.status?.toUpperCase();

        // Map status from validation report to ValidationResult status
        let status: "pass" | "fail" | "warning" | "info" = "warning";
        if (regStatus === "PASS") {
          status = "pass";
        } else if (regStatus === "FAIL") {
          status = "fail";
        } else if (
          regStatus === "WARNING" ||
          regStatus === "NOT_TESTED_MISSING_ELEMENTS" ||
          regStatus === "NOT_APPLICABLE"
        ) {
          status = "warning";
        } else {
          status = "info";
        }

        // Extract element ID from entities_used if available
        let elementId = 0;
        if (regulation.entities_used && regulation.entities_used.length > 0) {
          const firstEntity = regulation.entities_used[0];
          elementId =
            firstEntity.dbId ||
            firstEntity.elementId ||
            parseInt(toiletId) ||
            0;
        } else {
          elementId = parseInt(toiletId) || 0;
        }

        results.push({
          ruleId: `ada-604-${regulation.regulation_id}`,
          ruleName: regulation.title || regulation.regulation_id,
          standard:
            report.validation_metadata?.standard ||
            report.metadata?.standard ||
            report.standard ||
            "ICC A117.1-2017",
          status: status,
          elementId: elementId,
          elementName: `Toilet ${toiletId} - ${regulation.title || regulation.regulation_id}`,
          message: `${regulation.regulation_id}: ${regulation.title || "No title"}. ${regulation.measured_value ? `Measured: ${regulation.measured_value}` : ""} ${regulation.required_value ? `Required: ${regulation.required_value}` : ""}`,
          data: {
            details: {
              regulationId: regulation.regulation_id,
              section:
                regulation.section ||
                regulation.regulation_id?.split(".").slice(0, 2).join("."),
              status: regulation.status,
              measuredValue: regulation.measured_value,
              requiredValue: regulation.required_value,
              jerseySpecific: regulation.jersey_specific,
              notes: regulation.notes,
              confidence: regulation.confidence,
              severity: regulation.severity,
              entitiesUsed: regulation.entities_used || [],
              toiletElementId: toiletId,
            },
          },
          timestamp: new Date(),
        });
      });
    });

    return results;
  }, [validationReports]);

  const handleExportReport = async () => {
    setError(null);

    // Check if there are validation results to report
    // Use store validation results if available, otherwise use loaded reports
    const resultsToUse =
      storeValidationResults && storeValidationResults.length > 0
        ? storeValidationResults
        : validationResults;

    if (!resultsToUse || resultsToUse.length === 0) {
      setError(
        "No validation results available. Please run compliance validation first."
      );
      return;
    }

    try {
      setIsGenerating(true);

      // Simulate generation delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Show email popup instead of directly opening PDF
      setShowEmailPopup(true);
    } catch {
      setError("Failed to generate report. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  // Calculate summary statistics (same logic as RiskPanel - only count testable results)
  const stats = useMemo(() => {
    if (!validationResults || validationResults.length === 0) {
      return null;
    }

    // Only count testable results (pass/warning/fail), exclude "info" status (NOT_APPLICABLE, etc.)
    const testableResults = validationResults.filter(
      (r) =>
        r.status === "pass" || r.status === "warning" || r.status === "fail"
    );

    const total = testableResults.length;
    const passed = testableResults.filter((r) => r.status === "pass").length;
    const failed = testableResults.filter((r) => r.status === "fail").length;
    const warnings = testableResults.filter(
      (r) => r.status === "warning"
    ).length;
    const passRate = total > 0 ? ((passed / total) * 100).toFixed(1) : "0";

    return {
      total,
      passed,
      failed,
      warnings,
      passRate,
    };
  }, [validationResults]);

  const handleNavigateToCelebration = () => {
    if (onNavigateToCelebration) {
      onNavigateToCelebration();
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-custom-light-200 p-6">
        <h2 className="text-xl font-semibold text-custom-navy mb-2">
          Compliance Report Generation
        </h2>
        <p className="text-sm text-custom-grey">
          Generate comprehensive compliance reports in PDF format with detailed
          validation results and recommendations.
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-8 bg-custom-light-50">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg p-8 border border-custom-light-200 shadow-sm">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-custom-navy mb-2">
                Export Compliance Report
              </h3>

              {isLoadingReports ? (
                <p className="text-sm text-custom-grey mt-4 animate-pulse">
                  Loading validation results...
                </p>
              ) : !stats ? (
                <p className="text-sm text-custom-grey mt-4">
                  No validation results available yet
                </p>
              ) : null}
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Next Steps - placeholder for future functionality */}
            <button
              disabled
              className="w-full mb-3 py-3 px-4 rounded-lg font-medium bg-custom-light-200 text-custom-grey cursor-not-allowed"
            >
              Next Steps
            </button>

            <button
              onClick={handleExportReport}
              disabled={isGenerating || !hasRunValidation}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                isGenerating || !hasRunValidation
                  ? "bg-custom-light-200 text-custom-grey cursor-not-allowed"
                  : "btn btn-primary"
              }`}
            >
              {isGenerating ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⟳</span>
                  Generating Report...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Export Report
                </span>
              )}
            </button>

            {/* Manage All Risks Button */}
            {onNavigateToRisks && (
              <button
                onClick={onNavigateToRisks}
                disabled={!hasRunValidation}
                className={`w-full mt-3 py-3 px-4 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  !hasRunValidation
                    ? "bg-custom-light-200 text-custom-grey cursor-not-allowed"
                    : "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500"
                }`}
              >
                <span className="flex items-center justify-center gap-2">
                  Manage All Risks
                </span>
              </button>
            )}

            {/* Help Button */}
            <button
              onClick={() => setShowHelp(true)}
              className="w-full mt-3 py-3 px-4 rounded-lg font-medium text-white bg-custom-navy hover:bg-custom-dark transition-all duration-200 shadow-sm"
            >
              Help
            </button>
          </div>
        </div>
      </div>

      {/* Email Popup Modal */}
      <EmailPopupModal
        isOpen={showEmailPopup}
        onClose={() => setShowEmailPopup(false)}
        reportData={{
          validationResults:
            storeValidationResults && storeValidationResults.length > 0
              ? storeValidationResults
              : validationResults,
          validationSummary,
        }}
        reportType={getPDFForActiveRules(getActiveRules()).displayName}
        pdfAttachment={{
          // Always send a single, fixed PDF file via the email service
          filename: "HeroCon output.pdf",
        }}
        onNavigateToCelebration={handleNavigateToCelebration}
      />

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col m-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 bg-custom-navy border-b border-custom-dark">
              <h2 className="text-xl font-semibold text-white">
                Help & Support
              </h2>
              <button
                onClick={() => setShowHelp(false)}
                className="text-white hover:text-custom-light-100 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <HelpPanel />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * IMPLEMENTATION NOTES FOR FUTURE REPORT GENERATION:
 *
 * 1. Server-Side Generation:
 *    - Create API endpoint for report generation
 *    - Use libraries like jsPDF, PDFKit, or Puppeteer
 *    - Include screenshots from 3D viewer
 *    - Generate charts and graphs
 *
 * 2. Report Templates:
 *    - Create customizable report templates
 *    - Support different report types (summary, detailed, executive)
 *    - Allow branding customization
 *
 * 3. Data Processing:
 *    - Aggregate validation results by category
 *    - Calculate compliance scores
 *    - Generate recommendations based on violations
 *    - Include remediation cost estimates
 *
 * 4. Export Options:
 *    - PDF (primary)
 *    - Excel spreadsheet with raw data
 *    - Word document for editing
 *    - Interactive HTML report
 *
 * 5. Database Integration:
 *    - Store generated reports
 *    - Track report history
 *    - Enable report sharing via links
 *    - Schedule automated report generation
 */
